import React from 'react';
import RevealText from '../RevealText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Menu } from 'lucide-react';
import { HERO_IMAGE, AI_BG } from '../../mock';
import LiquidMaskReveal from '../LiquidMaskReveal';
import CircularText from '../CircularText';


const HERO_IMG = HERO_IMAGE;

// Stagger entry — fade + slide up (Framer-style)
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const item = {
  hidden: { opacity: 0, y: 50 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.08 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const bottomNameVariant = {
  hidden: { opacity: 0, y: 140 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.55 },
  },
};

const pillVariant = {
  hidden: { opacity: 0, y: -30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 },
  },
};

export default function Hero() {
  return (
    <section
      id="home"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: 'var(--bg)', minHeight: '100vh' }}
    >

      {/* Center portrait image */}
      <motion.div
        variants={imageVariant}
        initial="hidden"
        animate="show"
        className="absolute inset-0 z-[10] flex items-start justify-center"
      >
        <LiquidMaskReveal
          baseImage={HERO_IMAGE}
          revealImage={AI_BG}
          alt="Maycon Robert - Designer Gráfico e Web Designer"
        />
        {/* Side gradients to blend image edges into dark bg */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, var(--bg) 0%, rgba(0,0,0,0.55) 18%, rgba(0,0,0,0) 35%, rgba(0,0,0,0) 65%, rgba(0,0,0,0.55) 82%, var(--bg) 100%)',
          }}

        />
      </motion.div>

      {/* Gradient behind the big name for transition */}
      <div 
        className="absolute bottom-0 left-0 w-full h-[40vh] pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to top, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 35%, rgba(0, 0, 0, 0) 100%)'
        }}
      />


      {/* Content grid */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-[1200] max-w-[1500px] mx-auto px-6 md:px-10 pt-[60vh] md:pt-40 pb-20 md:pb-0 min-h-screen flex flex-col pointer-events-none"
      >
        {/* Mobile/Tablet circular badge: to the right of model's face, slightly below header */}
        <motion.div
          variants={item}
          className="lg:hidden absolute top-[13vh] right-[4vw] md:right-[15vw] z-[1300] pointer-events-auto"
        >
          <CircularText
            text="PASSE O DEDO SOBRE A IMAGEM • "
            onHover="speedUp"
            spinDuration={25}
            radius={42}
            className="text-[var(--accent)] bg-black/45 backdrop-blur-md rounded-full border border-white/5"
          />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 flex-1 items-start">

          {/* LEFT */}
          <div className="lg:col-span-4 flex flex-col pointer-events-auto">
            {/* Available badge */}
            <motion.div variants={item} className="mb-10">
              <div
                className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full"
                style={{
                  backgroundColor: 'rgba(38,38,38,0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--accent)] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--accent)]" />
                </span>
                <span
                  className="text-white"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >

                  Disponível para trabalho
                </span>
              </div>
            </motion.div>

            {/* Big title */}
            <motion.h1
              variants={item}
              className="text-white"
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 500,

                fontSize: 'clamp(40px, 4.6vw, 68px)',
                lineHeight: 1.05,
                letterSpacing: '-0.02em',
              }}
            >
              Designer gráfico e web há 18 anos
            </motion.h1>
          </div>

          {/* MIDDLE — empty spacer (image is absolute behind) */}
          <div className="hidden lg:block lg:col-span-4" />

          {/* RIGHT */}
          <div className="lg:col-span-4 flex flex-col lg:items-start lg:pt-24 pointer-events-auto">
            <motion.div variants={item}>
              <CircularText
                text="PASSE O MOUSE SOBRE A IMAGEM • "
                onHover="speedUp"
                spinDuration={25}
                radius={50}
                className="hidden lg:flex mb-6 !mx-0 self-start text-[var(--accent)] bg-black/45 backdrop-blur-md rounded-full border border-white/5"
              />
            </motion.div>
            <motion.p
              variants={item}

              className="text-white max-w-sm"
              style={{
                fontFamily: 'var(--font-main)',
                fontWeight: 400,

                fontSize: 'clamp(15px, 1.05vw, 18px)',
                lineHeight: 1.5,
              }}
            >
              Olá, sou Maycon Robert– designer gráfico e web apaixonado por criar experiências visuais perfeitas que conectam e convertem.
            </motion.p>

            <motion.div variants={item} className="mt-9">
              <a
                href="#works"
                className="group relative inline-flex items-center gap-4 rounded-full transition-shadow"
                style={{
                  backgroundColor: 'var(--accent)',
                  color: '#fff',
                  padding: '12px 36px 12px 12px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 17,
                  fontWeight: 500,
                  boxShadow: '0 12px 32px -10px rgba(255,90,31,0.55)',
                }}
              >
                <span
                  className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white transition-transform group-hover:rotate-[-45deg]"
                  style={{ color: 'var(--accent)' }}
                >

                  <ArrowRight size={20} strokeWidth={2.5} />
                </span>
                Veja meus trabalhos
              </a>
            </motion.div>
          </div>
        </div>

        {/* BOTTOM — Giant "Dominic" */}
        <div className="hidden md:block relative w-full pointer-events-none mt-auto">
          <motion.h2
            variants={bottomNameVariant}
            initial="hidden"
            animate="show"
            className="text-white leading-[0.82] whitespace-nowrap text-center relative z-20"
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 500,

              fontSize: 'clamp(140px, 27vw, 460px)',
              letterSpacing: '-0.05em',
              transform: 'translateY(0%)',
            }}
          >
            Maycon
          </motion.h2>
        </div>
      </motion.div>

    </section>
  );
}
